import { useProductListing } from "src/hooks/algolia";
import {
  NoResultsWrapper,
  PaginationWrapper,
  ProductGridContainer,
  StyledAuthBanner,
} from "./ProductList.styled";
import { ProductsGrid } from "src/atoms/ProductsGrid";
import { generateProductCardProps } from "src/utils/product-listing-helpers";
import { ProductCardWishlist } from "../ProductCardWishlist";
import { SeperatorMessage } from "src/molecules/SeperatorMessage";
import { t, Trans } from "@lingui/macro";
import { NoProductsFound } from "src/molecules/NoProductsFound";
import { Button } from "src/atoms/Button";
import { Flex, useBreakpoints } from "@otrium/core";
import { theme } from "src/theme";
import { useLingui } from "@lingui/react";
import { GridSkeleton } from "./GridSkeleton";
import {
  Fragment,
  MouseEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Facets } from "./Facets";
import { PLPType, PLPTypes } from "src/types/PLPType.d";
import useFilterProductLabels from "src/hooks/useFilterProductLabels";
import { PageContentType } from "src/types/graphql.d";
import { AuthModule } from "../AuthModule";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { useNewIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { InstantSearchServerState } from "react-instantsearch-core-v7";
import { VirtualPagination } from "./VirtualPagination";
import { useOfferCatalogSchema } from "src/hooks/useOfferCatalogSchema";

type GridProps = {
  indexName: string;
  categoryPageId?: string;
  slug: string;
  gender: string;
  pageContentType: PLPTypes;
  excludeFilters?: string[];
  title?: string;
  hitsPerPage: number;
  serverState?: InstantSearchServerState;
  gridRef: MutableRefObject<HTMLDivElement | null>;
  hideCategoryTree?: boolean;
};

const NO_PADDING_PLP_TYPES: PLPTypes[] = ["COLLECTIONS_PLP"];

function Grid({
  indexName,
  gender,
  categoryPageId,
  pageContentType,
  excludeFilters,
  title,
  slug,
  hitsPerPage,
  serverState,
  gridRef,
  hideCategoryTree,
}: GridProps): JSX.Element {
  const isFirstRender = useRef(true);
  const {
    products,
    total,
    isLoading,
    hasNoResults,
    nextPage,
    cacheSize,
    showPreviousButton,
    isFirstPage,
    isLastPage,
    showMoreProducts,
    setVisibleProducts,
    showPreviousProducts,
    segmentProductOnClick,
  } = useProductListing({
    gender,
    hitsPerPage: 72,
    categoryPageId,
    pageType: pageContentType,
    indexName,
    title,
    serverState,
    gridRef,
  });
  const { isLoggedIn, isSessionLoading } = useNewIsLoggedIn();

  const { i18n } = useLingui();
  const { isMobile } = useBreakpoints();

  const { data: labels } = useFilterProductLabels({
    products,
    pageContentType:
      pageContentType === "BRAND_PLP"
        ? PageContentType.Brand
        : PageContentType.Category,
  });

  // Generate OfferCatalog schema for the product listing
  useOfferCatalogSchema({
    products,
    title,
    description: title ? `Shop ${title} at Otrium` : undefined,
    slug,
    gender,
    breadcrumbs: undefined, // You can add breadcrumbs here if available
  });

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  const authBannerInsertPosition = useMemo(
    () => (isMobile ? 2 : hideCategoryTree ? 4 : 3),
    [isMobile, hideCategoryTree]
  );

  const handleShowMore = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      showMoreProducts();
    },
    [showMoreProducts]
  );

  return (
    <ProductGridContainer
      className={hideCategoryTree ? "hideCategoryTree" : ""}
    >
      <Facets
        indexName={indexName}
        title={title}
        excludeFilters={excludeFilters}
        categoryPageId={categoryPageId}
        gender={gender}
        pageContentType={pageContentType}
        gridRef={gridRef}
      />

      {hasNoResults && !isFirstRender.current && !isLoading ? (
        <NoResultsWrapper>
          <NoProductsFound />
        </NoResultsWrapper>
      ) : null}

      {!hasNoResults ? (
        <>
          {!isFirstRender.current && showPreviousButton && !isFirstPage ? (
            <Flex
              wrap="wrap"
              variant="centered"
              flexDirection="column"
              gap={theme.space.space16}
              sx={{ gridColumn: "span 3" }}
            >
              <Button variant="secondary" onClick={showPreviousProducts}>
                <Trans>show_previous_products</Trans>
              </Button>
            </Flex>
          ) : null}
          <ProductsGrid isNewGrid hideCategoryTree={hideCategoryTree}>
            {products.map((product, idx) => {
              const productCardProps = generateProductCardProps(
                product,
                labels
              );
              const shouldInsertAuthBar =
                (idx + 1 - authBannerInsertPosition) % hitsPerPage === 0;

              return (
                <Fragment key={productCardProps.id}>
                  <ProductCardWishlist
                    position={idx}
                    product={productCardProps}
                    segmentProductOnClick={segmentProductOnClick}
                    setVisibleProducts={setVisibleProducts}
                  />
                  {shouldInsertAuthBar && !isSessionLoading && !isLoggedIn && (
                    <StyledAuthBanner hideCategoryTree={hideCategoryTree}>
                      <AuthModule
                        trackingData={{
                          categorySlug: slug,
                          pageType: PLPType.CATEGORY_PLP,
                          shopType: localeFix({ gender }),
                        }}
                        noMarging
                      />
                    </StyledAuthBanner>
                  )}
                </Fragment>
              );
            })}

            {(isLoading && !isFirstRender.current) ||
            (!hasNoResults && products.length === 0) ? (
              <GridSkeleton
                numberOfItems={
                  cacheSize ? hitsPerPage * cacheSize : hitsPerPage
                }
              />
            ) : null}
          </ProductsGrid>

          <PaginationWrapper
            className={`${
              NO_PADDING_PLP_TYPES.includes(pageContentType)
                ? "no-padding-plp"
                : ""
            }`}
          >
            <SeperatorMessage data-testid="total-products">
              {t(i18n)`Seen ${products.length} from ${total}`}
            </SeperatorMessage>

            {products.length < total &&
            nextPage &&
            nextPage > -1 &&
            !isLastPage ? (
              <a href={`?page=${nextPage + 1}`} onClick={handleShowMore}>
                <Button variant="secondary">
                  <Trans>Show next products</Trans>
                </Button>
              </a>
            ) : null}
          </PaginationWrapper>
        </>
      ) : null}

      <VirtualPagination />
    </ProductGridContainer>
  );
}

export { Grid };
